import { Node, Edge } from 'reactflow';

interface IRule {
    id: string;
    question: {
        id: string;
        title: string;
        type: string;
    };
    answer: {
        value: string | string[] | number;
        id: string | string[] | null;
        comparison?: {
            operator: string;
        };
    };
    result: {
        type: 'not_suitable' | 'text_visit' | 'continue' | 'add_note';
        skipToQuestionId?: string;
        selectedOptions?: string[];
        customText?: string;
    };
    triggerTiming: 'immediate' | 'after_last_question';
}

interface IJsonInput {
    assessmentUuid: string;
    rules: IRule[];
}

interface IFlowData {
    nodes: Node[];
    edges: Edge[];
    nodeConfigs: Record<string, any>;
}

/**
 * 将SimpleRuleBuilder的JSON格式转换为流程图数据
 */
export const jsonToFlow = (jsonData: IJsonInput): IFlowData => {
    const { rules } = jsonData;
    
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const nodeConfigs: Record<string, any> = {};
    
    // 添加开始节点
    const startNode: Node = {
        id: 'start_1',
        type: 'start',
        position: { x: 100, y: 100 },
        data: {
            type: 'start',
            label: 'Start',
        },
    };
    nodes.push(startNode);
    
    // 为每个规则创建问题节点和结果节点
    rules.forEach((rule, index) => {
        const questionNodeId = `question_${rule.id}`;
        const resultNodeId = `result_${rule.id}`;
        
        // 创建问题节点
        const questionNode: Node = {
            id: questionNodeId,
            type: 'question',
            position: { x: 300, y: 100 + index * 200 },
            data: {
                type: 'question',
                label: 'Question',
                questionTitle: rule.question.title,
            },
        };
        nodes.push(questionNode);
        
        // 创建结果节点
        const resultNode: Node = {
            id: resultNodeId,
            type: 'result',
            position: { x: 600, y: 100 + index * 200 },
            data: {
                type: 'result',
                label: 'Result',
                resultType: rule.result.type,
            },
        };
        nodes.push(resultNode);
        
        // 创建连线：开始 -> 问题
        if (index === 0) {
            const startToQuestionEdge: Edge = {
                id: `edge_start_${questionNodeId}`,
                source: 'start_1',
                target: questionNodeId,
                type: 'condition',
                data: {
                    condition: '',
                    label: '',
                },
            };
            edges.push(startToQuestionEdge);
        }
        
        // 创建连线：问题 -> 结果
        const questionToResultEdge: Edge = {
            id: `edge_${questionNodeId}_${resultNodeId}`,
            source: questionNodeId,
            target: resultNodeId,
            type: 'condition',
            data: {
                condition: rule.answer.value,
                label: String(rule.answer.value),
            },
        };
        edges.push(questionToResultEdge);
        
        // 如果是continue类型且有跳转目标，创建跳转节点
        if (rule.result.type === 'continue' && rule.result.skipToQuestionId) {
            const jumpNodeId = `jump_${rule.id}`;
            const jumpNode: Node = {
                id: jumpNodeId,
                type: 'jump',
                position: { x: 900, y: 100 + index * 200 },
                data: {
                    type: 'jump',
                    label: 'Jump',
                    targetQuestionId: rule.result.skipToQuestionId,
                },
            };
            nodes.push(jumpNode);
            
            // 创建连线：结果 -> 跳转
            const resultToJumpEdge: Edge = {
                id: `edge_${resultNodeId}_${jumpNodeId}`,
                source: resultNodeId,
                target: jumpNodeId,
                type: 'condition',
                data: {
                    condition: '',
                    label: 'Jump',
                },
            };
            edges.push(resultToJumpEdge);
        }
        
        // 保存节点配置
        nodeConfigs[questionNodeId] = {
            type: 'question',
            questionId: rule.question.id,
            answerCondition: rule.answer.value,
            triggerTiming: rule.triggerTiming,
        };
        
        nodeConfigs[resultNodeId] = {
            type: 'result',
            resultType: rule.result.type,
            skipToQuestionId: rule.result.skipToQuestionId,
            selectedOptions: rule.result.selectedOptions,
            customText: rule.result.customText,
            triggerTiming: rule.triggerTiming,
        };
        
        if (rule.result.type === 'continue' && rule.result.skipToQuestionId) {
            nodeConfigs[`jump_${rule.id}`] = {
                type: 'jump',
                targetQuestionId: rule.result.skipToQuestionId,
                triggerTiming: rule.triggerTiming,
            };
        }
    });
    
    return {
        nodes,
        edges,
        nodeConfigs,
    };
};

/**
 * 验证JSON数据格式是否正确
 */
export const validateJsonFormat = (jsonData: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!jsonData) {
        errors.push('JSON data is required');
        return { isValid: false, errors };
    }
    
    if (!jsonData.assessmentUuid) {
        errors.push('assessmentUuid is required');
    }
    
    if (!Array.isArray(jsonData.rules)) {
        errors.push('rules must be an array');
        return { isValid: false, errors };
    }
    
    jsonData.rules.forEach((rule: any, index: number) => {
        if (!rule.id) {
            errors.push(`Rule ${index} is missing id`);
        }
        
        if (!rule.question || !rule.question.id || !rule.question.title) {
            errors.push(`Rule ${index} is missing question information`);
        }
        
        if (!rule.answer || rule.answer.value === undefined) {
            errors.push(`Rule ${index} is missing answer information`);
        }
        
        if (!rule.result || !rule.result.type) {
            errors.push(`Rule ${index} is missing result information`);
        }
        
        if (!rule.triggerTiming) {
            errors.push(`Rule ${index} is missing triggerTiming`);
        }
    });
    
    return {
        isValid: errors.length === 0,
        errors,
    };
};
